<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-4">
      <div class="flex gap-4 w-full max-w-2xl">
        <div class="flex-1">
          <label for="org-select" class="block text-sm font-medium mb-2">Organization</label>
          <Select
            id="org-select"
            v-model="selectedOrganizationId"
            :options="organizationOptions"
            optionLabel="name"
            optionValue="id"
            placeholder="Select Organization"
            class="w-full"
          />
        </div>

        <div class="flex-1">
          <label for="email-filter" class="block text-sm font-medium mb-2">User email</label>
          <InputText
            id="email-filter"
            v-model="emailFilter"
            placeholder="Filter by email..."
            class="w-full"
          />
        </div>
      </div>

      <DataTable
        v-if="users"
        :value="users"
        paginator
        :rows="50"
        :rowsPerPageOptions="[50, 100, 200]"
        tableStyle="min-width: 50rem"
        class="hoverable-rows"
      >
        <Column field="email" header="Email"></Column>
        <Column field="orgName" header="Organization"></Column>
        <Column>
          <template #body="{ data }">
            <div class="flex justify-end">
              <Button
                @click="impersonateUser(data)"
                severity="secondary"
                variant="text"
                label="Impersonate"
                size="small"
              ></Button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import type { OrganizationDto, UserDto } from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import { useAuth } from '@/modules/auth/auth';
import { useApiClient } from '@/api/apiClient';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';

const { api } = useApiClient();
const { impersonate } = useAuth();
const users = ref<UserDto[]>([]);
const organizations = ref<OrganizationDto[]>([]);
const selectedOrganizationId = ref<string>('');
const emailFilter = ref('');

const organizationOptions = computed(() => {
  return [
    { id: '', name: 'All organizations' },
    ...organizations.value.map(org => ({ id: org.id, name: org.name })),
  ];
});

async function loadUsers() {
  const { data, error } = await api.adminUserControllerList({
    query: {
      organizationId: selectedOrganizationId.value || undefined,
      email: emailFilter.value || undefined,
    },
  });
  if (error) {
    throw new Error('Failed to load users');
  }
  users.value = data ?? [];
}

async function loadOrganizations() {
  const { data, error } = await api.adminOrganizationControllerList();
  if (error) {
    throw new Error('Failed to load organizations');
  }
  organizations.value = data ?? [];
}

const debouncedLoadUsers = useDebounceFn(loadUsers, 300);

watch(selectedOrganizationId, () => {
  loadUsers();
});

watch(emailFilter, () => {
  debouncedLoadUsers();
});

loadUsers();
loadOrganizations();

function impersonateUser(user: UserDto) {
  impersonate(user.id);
}
</script>

<style scoped>
.hoverable-rows :deep(tbody tr) {
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.hoverable-rows :deep(tbody tr:hover) {
  background-color: #f9fafb;
}
</style>
